import type { ProAbonoSubscription } from '../services/paymentApi';
import type { BillingEntity } from './BillingEntity';
import type { ImportSource } from './ImportSource';
import { IMPORT_SOURCES  } from './ImportSource';
import type { PaymentStatus } from './PaymentStatus';
import type { SciMailWindowsMap } from './SciMail';
import type { Subcontractor } from './Subcontractor';
import type { User } from './User';

export interface MarketVisionSettings {
  [year: string]: string | number;
}

export interface ToolsTimelineEventPositions {
  referent: { [eventName: string]: number };
  'sci-referent': { [eventName: string]: number };
  boitage: { [eventName: string]: number };
  newsletter?: { [eventName: string]: { position: number; theme: string } };
  followup?: { [eventName: string]: number };
}

export interface ToolsTimelineEventActivation {
  referent: { [eventName: string]: boolean };
  'sci-referent': { [eventName: string]: boolean };
  boitage: { [eventName: string]: boolean };
}

export interface ToolsTimelineRowVisibility {
  [rowId: string]: boolean;
}

export interface ToolsTimelinePersonAssignments {
  [rowKey: string]: number | null;
}

export interface ToolsTimelineExactDates {
  [eventKey: string]: { day: number; month: number };
}

/**
 * Planning occurrences marked as done for the cycle, keyed like `exactDates`
 * (`${eventType}-${eventName}`). Written only by the dedicated endpoint.
 */
export interface ToolsTimelineCompletedEvents {
  [eventKey: string]: { completedAt: string; completedBy: number | null };
}

export interface ToolsTimelineYear {
  startMonth: number;
  eventPositions: ToolsTimelineEventPositions;
  eventActivation?: ToolsTimelineEventActivation;
  rowVisibility?: ToolsTimelineRowVisibility;
  personAssignments?: ToolsTimelinePersonAssignments;
  exactDates?: ToolsTimelineExactDates;
  completedEvents?: ToolsTimelineCompletedEvents;
}

export interface ToolsTimeline {
  [year: string]: ToolsTimelineYear;
}

export interface CompanyDataResponse {
  siren?: string;
  syndic: boolean;
  gesLoc: boolean;
  transa: boolean;
  loc: boolean;
  fnaim: boolean;
  unis: boolean;
  snpi: boolean;
  nbLotsHab: number | null;
  nbLotsHabBurCom: number | null;
  address: string;
  postalCode: string;
  city: string;
  departement: string;
  fnaimCity: string;
  fnaimPhone: string;
  fnaimWebsite: string;
  fnaimServices: string;
  fnaimAdditionalInfo: string;
  snpiPhone: string;
  snpiOwner: string;
  snpiUrl: string;
  snpiAdministrator: boolean; // Administrateur de biens
  snpiManager: boolean; // Syndic ??
  snpiHouseAgent: boolean; // Agent immobilier
  snpiExpert: boolean; // Expert immobilier
  transactionPappers?: boolean;
  gestionImmobilierePappers?: boolean;
  syndicPappers?: boolean;
  garants?: string;
  assureurs?: string;
  carte?: string;
  dateFinCarte?: string;
  infoDirigeant?: string;
}

export interface Garant {
  nom: string;
  montant_de_la_garantie: string;
}

export interface Assureur {
  assureur_nom: string;
}

export interface TransactionEmployeeRecord {
  userId: number;
  status: 'Transactionnaire' | 'Responsable du service';
  // legacy support (read-only):
  /**
   * @deprecated This field is read-only and only present for legacy support. Do not rely on its presence.
   */
  name?: string;
}

export interface TransactionAgentRecord {
  userId: number;
}

export interface GestionLocativeEmployeeRecord {
  userId: number;
  role: 'Responsable commercial du service' | 'Gestionnaire locatif' | 'Assistant gestionnaire locatif';
  // legacy support (read-only):
  name?: string;
}

export interface TransactionQualification {
  hasCarteT: boolean | null;
  willSignMandates: boolean | null;
  partnerAgenciesCount: number | null;
  transactionEmployees?: TransactionEmployeeRecord[] | null;
  transactionAgents?: TransactionAgentRecord[] | null;
  gestionLocativeEmployees?: GestionLocativeEmployeeRecord[] | null;
}

export interface PartnerAgencies {
  count: number | null;
  agencies: Agency[];
}

export interface Agency {
  siret: string;
  siren: string;
  cnilContractSigned: boolean;
  name: string;
  address: {
    street: string;
    postalCode: string;
    city: string;
  };
}

export interface DateTypeDJ {
  date: string;
  timezone_type: number;
  timezone: string;
}

export type BlogStructureType = 'straight-list' | 'alternated-list' | 'grid';

export type DpeFirstContactTemplateKey =
  | 'referent-only'
  | 'dpe-report'
  | 'copro-report'
  | 'both-reports'
  | 'transaction-v1'
  | 'transaction-v2';

export type ReferentCampaignTemplateKey =
  | 'model1-primary'
  | 'model1-alternative'
  | 'model2-primary'
  | 'model2-alternative'
  | 'model3-primary'
  | 'model3-alternative'
  | 'model4-primary'
  | 'model4-alternative'
  | 'model5-custom'
  /** @deprecated Legacy key kept for reading older branding selections. */
  | 'model4-custom';

/**
 * Custom "Modèle 5 — Personnalisé" document paths keyed by campaign index ("1"|"2"|"3").
 * Stored on `BrandingSetting.referentCampaignCustomDocs` server-side.
 */
export type ReferentCampaignCustomDocsMap = Partial<Record<'1' | '2' | '3', string>>;

export type AgCommunicationStrategy = 'referent-campaign' | 'copro-report';

export type DpeGeneralMailTemplateKey =
  | 'v1'
  | 'v2'
  | 'v3'
  | 'v4'
  | 'transaction-v1'
  | 'transaction-v2';

export interface ReferentMutation {
  id: string; // Unique identifier for each referent
  title: string;
  firstname: string;
  lastname: string;
  email?: string;
  phone?: string;
  photoName?: string;
  hasExternalNewsletter?: boolean; // Whether referent has an external newsletter website
  externalNewsletterUrl?: string; // URL to external newsletter (used for QR codes in PDFs)
  hasRgpdBlogArticle?: boolean; // Whether referent provides a dedicated RGPD blog article
  rgpdBlogArticleUrl?: string; // URL of the RGPD blog article (replaces /newsletter-dpe link in DPE letters/emails)
  userId?: number | null; // Linked system user ID (for SMTP, permissions, etc.)
  pdfIntroductionName?: string; // Per-referent cabinet intro PDF for report exports
}

export interface Company {
  id: number;
  name: string;
  siren: string;
  brandingSettings?: {
    color1: string;
    color2: string;
    color3: string;
    blogStructure: BlogStructureType;
    defaultTemplate?: string;
    // Multi-referent array (photos stored in referent.photoName)
    referents?: ReferentMutation[];
    emailSubcontractorName?: string;
    // Commercial name to use instead of legal name in client-facing contexts
    commercialName?: string;
    // Defaults to true for existing cabinets; controls public blog identity details.
    showLegalInfoOnBlog?: boolean;
    brandingImageName?: string;
    brandingBlogImageName?: string;
    // "Choix de contenu" preferences (Paramétrage > Contenu croissance)
    defaultDpeFirstContactTemplate?: DpeFirstContactTemplateKey;
    defaultReferentCampaign1?: ReferentCampaignTemplateKey;
    defaultReferentCampaign2?: ReferentCampaignTemplateKey;
    defaultReferentCampaign3?: ReferentCampaignTemplateKey;
    /**
     * Custom email text the user wants to use when sending the Référent
     * Mutation Location campaigns. Stored globally per company and shown as a
     * copy-paste reminder on the Campagne tab — NOT embedded in the generated
     * PDF (the PDF only contains the rendered email template).
     */
    referentCampaignEmailText?: string;
    /**
     * Per-campaign uploaded document (Modèle 5 — Personnalisé). Keyed by
     * campaign index ("1"|"2"|"3"); the value is the public-relative path of
     * the uploaded PDF / DOC / DOCX. Used when the matching
     * `defaultReferentCampaign{n}` is `model5-custom`.
     */
    referentCampaignCustomDocs?: ReferentCampaignCustomDocsMap;
    defaultAgCommunicationStrategy?: AgCommunicationStrategy;
    /**
     * Default template key for the "envoi général à tous les copropriétaires"
     * (DPE-driven) mailing. Used as the initial selection on the new
     * "Envois généraux DPE" tab in the Communication aux propriétaires page.
     */
    defaultDpeGeneralMailTemplate?: DpeGeneralMailTemplateKey;
    /**
     * Per upcoming SCI mail window choice — keyed by `${year}:${sciIndex}`
     * (sciIndex ∈ 1|2|3). Each entry stores the kind (rent or transaction)
     * and the chosen template key.
     */
    sciMailWindows?: SciMailWindowsMap;
  };
  proabonanoIframe?: string | null;
  /** @deprecated Use paymentStatus instead */
  hasPaidAccess: boolean;
  /** @deprecated Use proAbonoSubscriptionEnd instead */
  paidAccessUntil: string; // ISO date string
  // ProAbono Payment System
  proAbonoReference?: string | null;
  proAbonoCreateDate?: string | null;
  proAbonoSubscriptionEnd?: string | null;
  paymentStatus?: PaymentStatus | null;
  lastPaymentDate?: string | null;
  // Entity billing the company (ProAbono account), Déclarations Juridiques by default
  billingEntity?: BillingEntity;
  setupSteps: SetupSteps;
  commission: number;
  subcontractors: Subcontractor[];
  subcontractorFor: Subcontractor[];
  realSales: MarketVisionSettings;
  nbMandates: MarketVisionSettings;
  nbPreSaleStatements: MarketVisionSettings;
  pricePreSaleStatements: MarketVisionSettings;
  nbSaleStatements: MarketVisionSettings;
  anomalyNotifier: string;
  details: CompanyDataResponse;
  transactionQualification: TransactionQualification | null;
  mandatoryNewsletterSentAt?: string;
  DJUpdatedAt?: string;
  DJDeletedAt?: string;
  subscription?: ProAbonoSubscription | null;
  referentQualiteUser?: User | null;
  assistantReferentQualiteUser?: User | null;
  toolsTimeline?: ToolsTimeline | null;
  boitageSettings?: {
    /**
     * Per (year, eventIndex) boitage window document storage. Keys follow the
     * `${year}:${eventIndex}` pattern (eventIndex ∈ 1|2). Each entry stores the
     * relative public paths for the stop-pub and/or sans-stop-pub PDF.
     */
    windows?: Record<string, { 'stop-pub'?: string | null; 'sans-stop-pub'?: string | null }>;
  };
  reportSettings?: { PDFIntroduction?: string } | null;
  selectedSoftware?: SoftwareType | null;
  configExportFileName?: string | null;
  configExportUploadedAt?: string | null;
  importReportPdfFileName?: string | null;
  importReportPdfUploadedAt?: string | null;
}

export type SoftwareType = ImportSource | 'egide-even' | 'seiitra' | 'ics';

export const SOFTWARE_CONFIGS: { id: SoftwareType; displayName: string; hasPdf: boolean; pdfUrl?: string; implemented: boolean }[] = [
  { id: IMPORT_SOURCES.RNIC_EXCEL, displayName: 'FICHIER EXCEL - exporté depuis cette plateforme', hasPdf: false, implemented: true },
  { id: IMPORT_SOURCES.IMMOPEN, displayName: 'IMMOPEN', hasPdf: true, pdfUrl: '/downloads/IMMOPEN extraction  pour  PARAMETRAGE.pdf', implemented: true },
  { id: IMPORT_SOURCES.NEOTEEM, displayName: 'NEOTEMM', hasPdf: true, pdfUrl: '/downloads/neoteem methodologie export copro.pdf', implemented: true },
  { id: 'egide-even', displayName: 'EGIDE EVEN', hasPdf: true, pdfUrl: '/downloads/EGIDE EVEN EXPORT COPROPRIETES.pdf', implemented: false },
  { id: 'seiitra', displayName: 'SEIITRA', hasPdf: false, implemented: false },
  { id: 'ics', displayName: 'ICS', hasPdf: false, implemented: false },
];

interface SetupSteps {
  transactionQualification: boolean | null;
  coproValidation: boolean | null;
  addressValidation: boolean | null;
  addMissingCopros: boolean | null;
  surfaceValidation: boolean | null;
  collaboratorAttribution: boolean | null;
  mailAlertSetup: boolean | null;
  vigilanceAdditionalCopro: boolean | null;
  reportSettingsComplete: boolean | null;
  mailConfigSetup: boolean | null;
  sciTargetQualification: boolean | null;
  agDateQualification: boolean | null;
}

export type CompanySetupStepsKey = keyof SetupSteps;
export const setupStepOrder: CompanySetupStepsKey[] = [
  'coproValidation',
  'addMissingCopros',
  'addressValidation',
  'transactionQualification',
  'collaboratorAttribution',
  'mailAlertSetup',
  'vigilanceAdditionalCopro',
  'reportSettingsComplete',
  'mailConfigSetup',
  'sciTargetQualification',
  'agDateQualification',
];
